import React, { useEffect, useState } from 'react';
import { Card, Col, Empty, Row } from 'antd';
import styled from 'styled-components/macro';

import { getCampaignsHosts, getCampaignsUnits } from 'utils/apis/campaigns';
import { useFetchConstant } from 'utils/hooks/general';
import { useGetVoteDetails } from 'utils/hooks/campaign';
import { useFbUser } from 'utils/hooks/fb';

import CampaignCard from 'components/CampaignCard/CampaignCard';
import CampaignSignUpModal from 'components/CampaignSignUpModal/CampaignSignUpModal';
import ImageGallery from 'components/ImageGallery/ImageGallery';

import HostAwardImage from './images/host-award-banner.jpg';

const BannerContainer = styled(Row)`
  margin: -24px -11% 0 !important;
  height: auto;

  @media (min-width: 768px) {
    margin: 0 -11% !important;
  }
`;

const ListingCard = styled(Card)`
  flex-direction: column;
  background-color: ${props => props.theme.color.white} !important;
  margin: 3% auto auto auto !important;
  border: 0 !important;
  height: 100%;
  @media (min-width: 992px) {
    flex-direction: row;
  }
`;

const CardContainer = styled(Col)`
  margin-bottom: 32px;
`;

const useGetCampaignsTabsDetails = () => {
  const { originalSelection: campaignVotingTypesConstant, isLoading } = useFetchConstant('campaignVotingTypes');
  const [campaignVotingTypes, setCampaignVotingTypes] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState('');

  useEffect(() => {
    if (!isLoading) {
      const campaignTabs = Object.values(campaignVotingTypesConstant).map(campaign => ({
        key: campaign.code,
        tab:
          (campaign.code === campaignVotingTypesConstant.HOST.code && 'Most Favourite Host') ||
          (campaign.code === campaignVotingTypesConstant.UNIT.code && 'Most Instagrammable Unit') ||
          'Award'
      }));
      setCampaignVotingTypes(campaignTabs);
      campaignTabs && campaignTabs.length > 0 && setActiveTabKey(campaignTabs[0].key);
    }
  }, [isLoading, campaignVotingTypesConstant]);
  return { campaignVotingTypesConstant, campaignVotingTypes, activeTabKey, setActiveTabKey, isLoading };
};

const useGetCampaignsHosts = () => {
  const [campaignsHosts, setCampaignsHosts] = useState([]);
  const { selections: states, isLoading } = useFetchConstant('statesMY');

  useEffect(() => {
    if (!isLoading) {
      getCampaignsHosts().then(data => {
        const formattedCampaignsHosts = formatCampaignsHostsByCampaign(data, states);
        setCampaignsHosts(formattedCampaignsHosts);
      });
    }
  }, [isLoading, states]);
  return campaignsHosts;
};

const useGetCampaignsUnits = () => {
  const [campaignsUnits, setCampaignsUnits] = useState([]);

  useEffect(() => {
    getCampaignsUnits().then(data => {
      setCampaignsUnits(data);
    });
  }, []);
  return campaignsUnits;
};

const formatCampaignsHostsByCampaign = (campaignsHosts = [], states = []) => {
  return campaignsHosts.map(campaignHost => {
    if (campaignHost.coveredAreas && campaignHost.coveredAreas.length > 0) {
      campaignHost.coveredAreas = campaignHost.coveredAreas.map(coveredArea => {
        const foundState = states.find(state => String(state.code) === String(coveredArea));
        return foundState.label;
      });
    }
    return campaignHost;
  });
};

const HostAwardsListing = () => {
  const {
    campaignVotingTypesConstant,
    campaignVotingTypes,
    activeTabKey,
    setActiveTabKey,
    isLoading: isConstantLoading
  } = useGetCampaignsTabsDetails();
  const campaignsHosts = useGetCampaignsHosts();
  const campaignsUnits = useGetCampaignsUnits();

  const { user, loginFb } = useFbUser();
  const { votedHosts, votedUnits, saveAndSubmitVote } = useGetVoteDetails(user.id);
  const [selectedUnitPhotos, setSelectedUnitPhotos] = useState([]);

  const isInHostRelatedTab = !isConstantLoading && activeTabKey === campaignVotingTypesConstant.HOST.code;
  const isInUnitRelatedTab = !isConstantLoading && activeTabKey === campaignVotingTypesConstant.UNIT.code;
  const data = (isInHostRelatedTab && campaignsHosts) || (isInUnitRelatedTab && campaignsUnits) || [];

  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [tempSelectedType, setTempSelectedType] = useState('');
  const [tempSelectedCurrentItemId, setTempSelectedCurrentItemId] = useState('');

  useEffect(() => {
    if (tempSelectedType && tempSelectedCurrentItemId && user.isConnected) {
      saveAndSubmitVote(tempSelectedType, tempSelectedCurrentItemId, user);
      setTempSelectedType('');
      setTempSelectedCurrentItemId('');
    }
  }, [tempSelectedType, tempSelectedCurrentItemId, user, saveAndSubmitVote]);

  const handleOnVote = async (type, currentItemId) => {
    if (user.isConnected) {
      saveAndSubmitVote(type, currentItemId, user);
    } else {
      setIsSignUpModalOpen(true);
      setTempSelectedType(type);
      setTempSelectedCurrentItemId(currentItemId);
    }
  };

  const handleOnSignUpModalOk = async () => {
    setIsSignUpModalOpen(false);
    await loginFb();
  };

  const handleOnSignUpModalClose = () => {
    setIsSignUpModalOpen(false);
  };

  return (
    <>
      <Row>
        <BannerContainer>
          <img src={HostAwardImage} width="100%" height="auto" alt="Top host award 2019 banner" />
        </BannerContainer>
        <CampaignSignUpModal visible={isSignUpModalOpen} onOk={handleOnSignUpModalOk} onModalClose={handleOnSignUpModalClose} />
        <ListingCard tabList={campaignVotingTypes} activeTabKey={activeTabKey} onTabChange={key => setActiveTabKey(key)}>
          <Row type="flex" gutter={16}>
            {data.length > 0 ? (
              data.map(item => (
                <CardContainer span={24} md={12} lg={8} xl={6} xxl={4} key={item._id}>
                  <CampaignCard
                    type={isInHostRelatedTab ? campaignVotingTypesConstant.HOST.code : campaignVotingTypesConstant.UNIT.code}
                    data={item}
                    isVoted={isInHostRelatedTab ? !!votedHosts.includes(item._id) : !!votedUnits.includes(item._id)}
                    onVote={() =>
                      handleOnVote(isInHostRelatedTab ? campaignVotingTypesConstant.HOST.code : campaignVotingTypesConstant.UNIT.code, item._id)
                    }
                    onClickUnitPhoto={isInHostRelatedTab ? undefined : setSelectedUnitPhotos}
                  />
                </CardContainer>
              ))
            ) : (
              <Col span={24} style={{ padding: '32px' }}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: 'rgba(0,0,0,0.25)' }}>No nomination</span>} />
              </Col>
            )}
          </Row>
        </ListingCard>
      </Row>
      <ImageGallery photos={selectedUnitPhotos} visible={selectedUnitPhotos.length > 0} onCloseImageGallery={() => setSelectedUnitPhotos([])} />
    </>
  );
};

export default HostAwardsListing;
